import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { cn } from 'lib/utils';
import type { Team } from 'modules/teams/types';
import { GroupTile } from './GroupTile';

const Stat: React.FC<{ value: number; label: string }> = ({ value, label }) => (
  <span className="flex flex-col gap-px">
    <span className="text-[15px] font-semibold leading-5 tabular-nums text-foreground">{value}</span>
    <span className="text-xs text-muted-foreground">{label}</span>
  </span>
);

export interface GroupCardProps {
  team: Team;
  className?: string;
}

/** Stats are limited to what the teams list returns (members + collections). */
export const GroupCard: React.FC<GroupCardProps> = ({ team, className }) => {
  const { t } = useTranslation();

  return (
    <Link
      to={`/app/teams/${team.id}`}
      className={cn(
        'flex flex-col gap-3.5 rounded-lg border border-border bg-card p-4 transition-colors hover:border-foreground/20 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
        className,
      )}
    >
      <span className="flex min-w-0 items-center gap-3">
        <GroupTile name={team.name} />
        <span className="flex min-w-0 flex-col gap-0.5">
          <span className="truncate text-[15px] font-semibold leading-5 text-foreground">{team.name}</span>
          <span
            className={cn(
              'truncate text-[12.5px] leading-[18px]',
              team.description ? 'text-muted-foreground' : 'text-muted-foreground/70',
            )}
          >
            {team.description || t('teams.no_description')}
          </span>
        </span>
      </span>
      <span className="flex gap-7 border-t border-border pt-3">
        <Stat value={team.memberCount} label={t('groups_stat_members')} />
        <Stat value={team.collectionCount} label={t('groups_stat_collections')} />
      </span>
    </Link>
  );
};

export default GroupCard;
